/* eslint-disable react-hooks/exhaustive-deps */
import {
  CONFIGURE_VIEW,
  NUMBER_ZERO,
  VIEW_TYPE,
  areas,
} from 'constants/constants';
import { memo, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Fade } from 'react-awesome-reveal';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPriorityCustomers,
  getViewCards,
} from 'store/home/home-my-view-actions';
import DefaultMessage from '../message/DefaultMessage';
import Tile from './Tile';
import classes from './Tiles.module.scss';
import { usePromiseTracker } from 'react-promise-tracker';
import { Sync } from '../loader/Sync';
import TilesLoadedMessage from '../message/TilesLoadedMessage';
import InfiniteScroll from 'react-infinite-scroll-component';
import { calculateItemsPerPage, getArrayToStringArray } from 'utils/utils';
import { setCards, setDefaultCard } from 'store/home/homeMyViewSlice';

const TileContainer = memo(({ page }) => {
  const dispatch = useDispatch();

  const { unixId } = useSelector((state) => state.Auth.userClaimObject);
  const { roleId } = useSelector((state) => state.Auth.userClaimObject);
  const cards = useSelector((state) => state?.HomeMyView?.cards);
  const totalTileCount = useSelector(
    (state) => state?.HomeMyView?.totalTileCount
  );
  const refreshTiles = useSelector((state) => state?.HomeMyView?.refreshTiles);
  const defaultCards = useSelector((state) => state?.HomeMyView?.defaultCards);
  const selectedView = useSelector((state) => state?.HomeMyView?.selectedView);

  const userRestrictedEcosystemt = useSelector(
    (state) => state?.Auth?.userRestrictedEcosystemt
  );

  const defaultCustomerType = useSelector(
    (state) => state?.CustomerView?.defaultCustomerType
  );
  const { promiseInProgress } = usePromiseTracker({ area: areas.home });

  // Pagination state
  const scrollRef = useRef(null);
  const [offset, setOffset] = useState(NUMBER_ZERO);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [priorityCustomers, setPriorityCustomers] = useState([]);

  const items = page === CONFIGURE_VIEW.myDefault ? defaultCards : cards;

  // Disable browser scroll restoration and reset scroll on mount
  // To stop calling the getcards api for next set of pagination, only initial set of pagination should get called
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  // Load All tile and Refresh the page before getting cards data api, analytics api is getting called for all 20 cards
  useEffect(() => {
    const handleUnload = () => {
      const setAction =
        page === CONFIGURE_VIEW.myDefault ? setDefaultCard : setCards;
      dispatch(setAction([]));
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Calculate items per page based on viewport dimensions
  const itemsPerPage = useMemo(
    () => calculateItemsPerPage(),
    [window.innerHeight, window.innerWidth]
  );

  useEffect(() => {
    setOffset(NUMBER_ZERO);
    setInitialLoadComplete(false);

    if (page === CONFIGURE_VIEW.myView) {
      fetchCards(NUMBER_ZERO);
    }
  }, [selectedView?.Id, refreshTiles]);

  useEffect(() => {
    setOffset(NUMBER_ZERO);
    setInitialLoadComplete(false);

    if (page === CONFIGURE_VIEW.myDefault) {
      fetchCards(NUMBER_ZERO);
    }
  }, [selectedView.Id]);

  const fetchCards = async (offset) => {
    if (offset === 0) {
      const ecosystems = getArrayToStringArray(userRestrictedEcosystemt);
      const params = [
        true,
        '',
        0,
        roleId,
        unixId,
        '',
        ecosystems,
        defaultCustomerType,
        '',
        '',
        '',
        true,
      ];
      const result = await getPriorityCustomers(params);
      setPriorityCustomers(result);
    }

    const payload = {
      roleId,
      viewId: selectedView?.Id,
      viewType:
        page === CONFIGURE_VIEW.myDefault
          ? VIEW_TYPE.SAMPLE
          : VIEW_TYPE.GENERAL,
      offset: offset,
      limit: itemsPerPage,
    };

    dispatch(getViewCards(payload)).then((result) => {
      setOffset(payload.offset);
      setInitialLoadComplete(true);
      if (payload.offset > 0) {
        setIsLoadingMore(false);
      }
    });
  };

  const fetchMoreData = useCallback(async () => {
    // Prevent fetching if initial load hasn't completed yet or already loading
    if (!initialLoadComplete || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    const newOffset = offset + itemsPerPage;

    await fetchCards(newOffset);

    // Reset loading state after a short delay to prevent rapid duplicate calls
  }, [initialLoadComplete, isLoadingMore, offset, itemsPerPage]);

  // Check if there are more items to load
  const hasMore = items.length < totalTileCount;

  return (
    <>
      {((page === CONFIGURE_VIEW.myView && cards?.length > 0) ||
        (page === CONFIGURE_VIEW.myDefault && defaultCards?.length > 0)) && (
        <div
          className={
            classes[
              ('tile-container-drag-drop-container',
              'infinite-scroll-container')
            ]
          }
          ref={scrollRef}>
          <Row className='list-manager g-3'>
            <InfiniteScroll
              dataLength={items.length}
              next={fetchMoreData}
              hasMore={hasMore && initialLoadComplete}
              loader={null}
              endMessage={<TilesLoadedMessage />}
              scrollThreshold={0.7} // Trigger when 70% scrolled
            >
              <Row className='list-manager g-3'>
                {items?.map((item, index) => (
                  <Col
                    key={item.cardId}
                    sm={12}
                    md={6}
                    xxl={4}>
                    <Tile
                      card={item}
                      page={page}
                      area={item.cardId}
                      priorityCustomers={priorityCustomers}
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          </Row>
        </div>
      )}

      {promiseInProgress && (
        <div className='d-flex justify-content-center'>
          <p className={`title title__normal ${classes['loading-text']}`}>
            Loading
          </p>
          <Sync area={areas.home} />
        </div>
      )}

      {totalTileCount === 0 &&
        !promiseInProgress &&
        page !== CONFIGURE_VIEW.myDefault && (
          <Fade>
            <DefaultMessage page={page} />
          </Fade>
        )}
    </>
  );
});

export default TileContainer;
